import React, { useState } from 'react';
import { ChevronDown, AlertTriangle } from 'lucide-react';
import { StatusUpdateModal } from './StatusUpdateModal';
import { useAuthStore } from '../../store/authStore';
import { ProjectStatus, ProfileType } from '../../types';

interface StatusSelectorProps {
  currentStatus: ProjectStatus;
  projectId: string;
  onStatusUpdate: (status: ProjectStatus, note?: string) => Promise<void>;
  isLoading?: boolean;
}

const STATUS_ORDER: ProjectStatus[] = [
  ProjectStatus.A_PREPARER,
  ProjectStatus.SOUMIS,
  ProjectStatus.ACCORD_PROVISOIRE,
  ProjectStatus.VALIDE,
  ProjectStatus.DEBLOCAGE_FONDS,
  ProjectStatus.EN_COURS,
  ProjectStatus.VERIFICATION_FINALE,
  ProjectStatus.TERMINE,
  ProjectStatus.REJETE,
  ProjectStatus.SUSPENDU
];

const STATUS_LABELS: Record<ProjectStatus, string> = {
  [ProjectStatus.A_PREPARER]: 'À préparer',
  [ProjectStatus.SOUMIS]: 'Soumis',
  [ProjectStatus.ACCORD_PROVISOIRE]: 'Accord provisoire',
  [ProjectStatus.VALIDE]: 'Validé',
  [ProjectStatus.DEBLOCAGE_FONDS]: 'Déblocage fonds',
  [ProjectStatus.EN_COURS]: 'En cours',
  [ProjectStatus.VERIFICATION_FINALE]: 'Vérification finale',
  [ProjectStatus.TERMINE]: 'Terminé',
  [ProjectStatus.REJETE]: 'Rejeté',
  [ProjectStatus.SUSPENDU]: 'Suspendu'
};

export const StatusSelector: React.FC<StatusSelectorProps> = ({
  currentStatus,
  projectId,
  onStatusUpdate,
  isLoading = false
}) => {
  const { user } = useAuthStore();
  const [selectedStatus, setSelectedStatus] = useState<ProjectStatus>(currentStatus);
  const [showModal, setShowModal] = useState(false);

  // Check if user can update status
  const canUpdateStatus = user && [ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user.profile_type as ProfileType);

  if (!canUpdateStatus) {
    return null;
  }

  const handleStatusChange = (newStatus: ProjectStatus) => {
    setSelectedStatus(newStatus);
    if (newStatus !== currentStatus) {
      setShowModal(true);
    }
  };

  const handleConfirmUpdate = async (note?: string) => {
    try {
      await onStatusUpdate(selectedStatus, note);
      setShowModal(false);
    } catch (error) {
      console.error('Error updating status:', error);
      // Reset selection on error
      setSelectedStatus(currentStatus);
      throw error;
    }
  };

  const handleCancel = () => {
    setSelectedStatus(currentStatus);
    setShowModal(false);
  };

  const requiresNote = selectedStatus === ProjectStatus.REJETE || selectedStatus === ProjectStatus.SUSPENDU;

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Gestion du statut</h3>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="status-select" className="block text-sm font-medium text-gray-700 mb-2">
              Statut actuel
            </label>
            <div className="relative">
              <select
                id="status-select"
                value={selectedStatus}
                onChange={(e) => handleStatusChange(e.target.value as ProjectStatus)}
                disabled={isLoading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white disabled:opacity-50"
              >
                {STATUS_ORDER.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {selectedStatus !== currentStatus && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Changement de statut détecté
                  </p>
                  <p className="text-sm text-blue-800 mt-1">
                    Le statut passera de "{STATUS_LABELS[currentStatus]}" à "{STATUS_LABELS[selectedStatus]}".
                    Une notification sera envoyée au porteur du projet.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Status Change Rules */}
          <div className="text-xs text-gray-500 space-y-1">
            <p>• Les statuts "Rejeté" et "Suspendu" nécessitent une note explicative</p>
            <p>• Un projet ne peut être "Terminé" sans passer par "Vérification finale"</p>
            <p>• Un projet "Terminé" ne peut pas être rejeté</p>
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      <StatusUpdateModal
        isOpen={showModal}
        onClose={handleCancel}
        onConfirm={handleConfirmUpdate}
        currentStatus={currentStatus}
        newStatus={selectedStatus}
        requiresNote={requiresNote}
        isLoading={isLoading}
      />
    </>
  );
};